import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { EcgLine } from "@/components/EcgLine";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const HERO_IMG =
  "https://images.unsplash.com/photo-1625134673337-519d4d10b313?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxjYXJkaWFjJTIwZG9jdG9yJTIwc3BlY2lhbGlzdCUyMGNsaW5pYyUyMGhvc3BpdGFsJTIwbWVkaWNhbHxlbnwwfHx8fDE3ODk1MDQwNDB8MA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const { scrollY } = useScroll();
  const parallaxY = useTransform(scrollY, [0, 700], [0, 90]);
  const [activeService, setActiveService] = useState(null);

  return (
    <section className="relative min-h-screen bg-ivory paper-grain overflow-hidden flex items-center" data-testid="hero-section">
      <div className="absolute top-0 right-0 w-1/3 h-full bg-navy/[0.025] border-l border-navy/5 hidden lg:block" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-20 relative z-10 w-full">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-14" data-testid="hero-audience-boxes">
          {[
            ["Private GP", "/private-gp", "On-site echocardiography, ECG, Holter and ABPM delivered inside your own practice — including one-stop clinics."],
            ["Corporate Heart Screening", "/corporate-screening", "Evidence-based cardiovascular screening delivered on your premises — blood pressure, AF detection, ECG and echocardiography on clinical indication."],
            ["NHS Primary Care", "/nhs-primary-care", "Community echocardiography delivered within primary care — hospital-standard reporting, shorter waits and fewer secondary care referrals."],
            ["Clinical Research", "/clinical-research", "Protocol-specific cardiac imaging for NIHR studies, sponsors and CROs — single accredited operator, core lab reads, GCP-compliant data."],
            ["Industry & Academic", "/industry-academic", "Reference-standard physiological measurement for device and endpoint validation — wearables tested against ECG, Holter, ABPM and echocardiography."],
          ].map(([label, href, tip]) =>
            href ? (
              <div key={label} className="relative group">
                <Link
                  to={href}
                  data-testid={`audience-box-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="block bg-navy text-ivory font-mono text-[10px] font-bold tracking-[0.15em] uppercase text-center py-5 hover:bg-gold hover:text-navy transition-colors"
                >
                  {label}
                </Link>
                <div
                  className="absolute top-full left-0 mt-2 w-72 bg-ivory border border-gold/40 shadow-2xl px-5 py-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-[opacity,visibility] duration-300 z-30 pointer-events-none"
                  data-testid={`audience-box-tooltip-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-gold mb-2">
                    {label}
                  </p>
                  <p className="text-[11px] text-navy/70 leading-relaxed normal-case tracking-normal font-sans font-normal">
                    {tip}
                  </p>
                  <p className="mt-2 font-mono text-[9px] tracking-[0.25em] uppercase text-gold">
                    Click for More Info →
                  </p>
                </div>
              </div>
            ) : (
              <div
                key={label}
                data-testid={`audience-box-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-navy text-ivory font-mono text-[10px] font-bold tracking-[0.15em] uppercase text-center py-5"
              >
                {label}
              </div>
            )
          )}
        </div>
        <div className="grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="font-mono text-[13px] font-medium tracking-[0.22em] uppercase text-gold mb-8"
            data-testid="hero-eyebrow"
          >
            Cardio-Vascular Imaging (UK) Ltd · Est. 2014
          </motion.p>

          <h1
            className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold tracking-tight leading-[1.05] text-navy"
            data-testid="hero-heading"
          >
            <MaskedLine delay={0.35}>Specialist Cardiac</MaskedLine>
            <MaskedLine delay={0.5}>Diagnostics.</MaskedLine>
            <MaskedLine delay={0.65}>
              <em className="font-medium">Integrated with Your</em>
            </MaskedLine>
            <MaskedLine delay={0.8}>
              <em className="font-medium">Clinical Service.</em>
            </MaskedLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-8 max-w-xl"
            data-testid="hero-subcopy"
          >
            <p className="font-serif italic text-xl lg:text-2xl text-navy leading-snug mb-6">
              Specialist cardiac diagnostics, delivered where your patients already are.
            </p>
            <p className="text-sm lg:text-base text-navy/60 leading-relaxed mb-4">
              Cardio-Vascular Imaging (UK) Ltd provides echocardiography and cardiac
              diagnostic services to private GP practices, NHS primary care, employers
              and clinical research teams.
            </p>
            <p className="text-sm lg:text-base text-navy/60 leading-relaxed mb-4">
              Every investigation is performed and reported by a Cardiac Physiologist on
              the Registration Council for Clinical Physiologists (RCCP) register —
              accredited in transthoracic echocardiography by the British Society of
              Echocardiography (BSE) and the European Association of Cardiovascular
              Imaging (EACVI).
            </p>
            <p className="text-sm lg:text-base text-navy font-medium leading-relaxed border-l-2 border-gold pl-5">
              We bring the specialist equipment and expertise to your site. You keep your
              pathway, your records and your relationship with the patient.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.35 }}
            className="mt-10 border-t border-navy/15 max-w-xl"
            data-testid="hero-whatwedo"
          >
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold pt-4">
              What We Do
            </p>
            {[
              ["Echocardiogram", "Transthoracic echocardiography (TTE) — full British Society of Echocardiography dataset studies with a written report, including advanced imaging such as strain analysis and advanced measurements for research."],
              ["12-Lead ECG", "Resting ECG performed as part of a diagnostic pathway or study protocol."],
              ["Ambulatory ECG (Holter)", "24-hour to 28-day monitoring for palpitations, syncope and suspected arrhythmia, with a fully analysed report."],
              ["Ambulatory BP (ABPM)", "24-hour ambulatory blood pressure monitoring with interpretation against NICE thresholds."],
            ].map(([name, detail], i) => {
              const open = activeService === i;
              return (
                <div
                  key={name}
                  className="border-b border-navy/15"
                  onMouseEnter={() => setActiveService(i)}
                  onMouseLeave={() => setActiveService(null)}
                  onClick={() => setActiveService(open ? null : i)}
                  data-testid={`whatwedo-item-${i}`}
                >
                  <div className="flex items-center gap-5 py-3 cursor-pointer">
                    <span className="font-mono text-[10px] tracking-[0.25em] text-gold">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className={`font-serif text-lg font-semibold transition-colors ${open ? "text-gold" : "text-navy"}`}>
                      {name}
                    </span>
                  </div>
                  <div
                    className={`overflow-hidden transition-[max-height,opacity] duration-500 ${
                      open ? "max-h-44 opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <p className="text-xs text-navy/60 leading-relaxed pl-10 pr-4 pb-4" data-testid={`whatwedo-detail-${i}`}>
                      {detail}
                    </p>
                  </div>
                </div>
              );
            })}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.6 }}
            className="mt-8 max-w-md"
          >
            <EcgLine className="text-gold h-12 w-full" />
          </motion.div>
        </div>

        <motion.div style={{ y: parallaxY }} className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -top-4 -left-4 w-full h-full border border-gold/60" />
            <img
              src={HERO_IMG}
              alt="Cardiac physiologist performing an echocardiogram"
              className="relative w-full h-[560px] object-cover grayscale-[20%]"
              data-testid="hero-image"
            />
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-8 -left-10 bg-navy text-ivory px-6 py-5 border border-gold/40 shadow-2xl"
              data-testid="hero-telemetry-card"
            >
              <p className="font-mono text-[10px] tracking-[0.3em] text-gold uppercase mb-2">
                Live Telemetry · 12-Lead ECG
              </p>
              <EcgLine className="text-champagne h-8 w-48" />
              <p className="font-mono text-[10px] text-ivory/60 mt-2">
                HR 72 BPM · SINUS RHYTHM · QTc 412ms
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
        </div>
      </div>
    </section>
  );
};
